import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import type { PartUpdateInput } from '@ncc/shared';
import { PartForm } from '../components/PartForm';
import { api } from '../lib/api';

export async function editPartLoader({
  params,
  context,
}: {
  params: { id: string };
  context: { queryClient: QueryClient };
}) {
  return context.queryClient.ensureQueryData({
    queryKey: ['parts', 'detail', Number(params.id)],
    queryFn: () => api.getPart(Number(params.id)),
  });
}

export function EditPart() {
  const { id } = useParams({ from: '/parts/edit/$id' });
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: part } = useQuery({
    queryKey: ['parts', 'detail', Number(id)],
    queryFn: () => api.getPart(Number(id)),
  });

  const save = useMutation({
    mutationFn: async ({
      patch,
      images,
    }: {
      patch: PartUpdateInput;
      images: { url: string; image_id?: string | null; position?: number }[];
    }) => {
      const updated = await api.updatePart(Number(id), patch);
      const finalState = await api.replacePartImages(Number(id), images);
      return finalState ?? updated;
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(['parts', 'detail', updated.id], updated);
      queryClient.invalidateQueries({ queryKey: ['parts'] });
    },
  });

  const remove = useMutation({
    mutationFn: () => api.deletePart(Number(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parts'] });
      navigate({ to: '/parts' });
    },
  });

  if (!part) return <div>Loading…</div>;

  return (
    <>
      <header className="toolbar">
        <h2 style={{ margin: 0 }}>
          <span className={`badge ${part.status}`} style={{ marginRight: 8 }}>
            {part.status.replace('_', ' ')}
          </span>
          {part.name}
        </h2>
        <span className="grow" />
        <button
          className="danger"
          onClick={() => {
            if (
              confirm(
                `Delete part "${part.name}" (${part.part_number})? This cannot be undone.`,
              )
            ) {
              remove.mutate();
            }
          }}
        >
          Delete
        </button>
        <Link to="/parts">
          <button className="ghost">Back</button>
        </Link>
      </header>

      <div className="card">
        <PartForm
          initial={part}
          submitLabel="Save changes"
          onSubmit={async (values, images) => {
            await save.mutateAsync({
              patch: values as PartUpdateInput,
              images,
            });
          }}
        />
      </div>

      {save.isPending ? <div className="toast">Saving…</div> : null}
    </>
  );
}
